import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import useUserContext from 'Context/UserContext';
import {
  useTemplatesDispatchContext,
  useTemplatesStateContext,
  SET_TEMPLATES,
  SET_PINNED_TEMPLATES,
  SET_TEMPLATES_LOADING,
} from 'Context/TemplateContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useDebounce from 'Hooks/useDebounce';
import { TEMPLATE_TYPES } from './utils';

const ERROR_STATUS_CONFLICT = 409;

const useTemplateOptionsMenu = (
  templateMenuNodeName,
) => {
  const {
    rootNodeProps,
    logResourceEvent,
    logResourceEventSuccess,
    logResourceEventFailureWithError,
  } = useEventTrackingLogEvent({
    nodeName: templateMenuNodeName,
  });

  // Dispatch Templates Context
  const dispatch = useTemplatesDispatchContext();
  const { setAllTemplatesCurrentPageNumber, templates, pinnedTemplates } = useTemplatesStateContext();
  const { agentResource } = useUserContext();
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();

  // Refresh Templates --------------------------------------------------------------------------------------------------------
  const refreshTemplates = async () => {
    try {
      const refreshedTemplates = await templates.link('self').get();
      dispatch({
        type: SET_TEMPLATES,
        templates: refreshedTemplates,
      });

      const refreshedPinnedTemplates = await pinnedTemplates.link('self').get();
      dispatch({
        type: SET_PINNED_TEMPLATES,
        pinnedTemplates: refreshedPinnedTemplates,
      });
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };


  // Search Templates --------------------------------------------------------------------------------------------------------
  const searchTemplatesWithDebounce = useDebounce(
    async (searchQuery, currentTemplates) => {
      dispatch({
        type: SET_TEMPLATES_LOADING,
        loading: true,
      });
      try {
        const templateLink = currentTemplates?.link('self').copy();
        if (searchQuery !== '') {
          templateLink.like('name', searchQuery);
        } else {
          templateLink.removeSearchParam('name');
        }

        const templateResults = await templateLink.get();
        dispatch({
          type: SET_TEMPLATES,
          templates: templateResults,
        });
        // Setting page number back to 1 because we are searching all templates, not just the current page
        setAllTemplatesCurrentPageNumber(1);
      } catch (err) {
        displayErrorMessage(err, err.errors);
      } finally {
        dispatch({
          type: SET_TEMPLATES_LOADING,
          loading: false,
        });
      }
    }, 500,
  );

  const searchTemplates = async (searchQuery) => {
    await searchTemplatesWithDebounce(searchQuery, templates);
  };

  // Save Template --------------------------------------------------------------------------------------------------------
  const saveTemplate = async (templateName, templateContent, templateSubject, templateType = TEMPLATE_TYPES.messageMeetingCombo) => {
    const eventName = 'Clicked Save Template';
    let templateToSave;
    try {
      // Create new message template resource
      templateToSave = await templates.create();
      logResourceEvent(eventName, templateToSave);

      // Set template attributes
      templateToSave
        .attr('name', templateName)
        .attr('content', templateContent)
        .attr('agentId', agentResource?.id());

      if (templateSubject) {
        templateToSave.attr('subject', templateSubject);
      }

      // Submit resource
      await templateToSave.submit({ linkArgs: { params: { templateType } } });

      // Refresh event
      await refreshTemplates();

      logResourceEventSuccess(eventName, templateToSave);
      displaySuccess(`"${templateName}" message template has been saved!`);
    } catch (err) {
      switch (err.status) {
        case ERROR_STATUS_CONFLICT:
          displayErrorMessage(err, `A ${templateType} template with this name already exists.`);
          break;
        default:
          displayErrorMessage(err, `There was an issue saving your ${templateType} template.`);
      }
      logResourceEventFailureWithError(eventName, templateToSave, err);
    }
  };

  // Remove Message Template --------------------------------------------------------------------------------------------------------
  const removeTemplate = async (templateID) => {
    const eventName = 'Clicked Remove Template';
    try {
      const templateToRemove = templates.resources().list().find(t => t.id() === templateID);
      logResourceEvent(eventName, templateToRemove);

      // Delete
      await templateToRemove.delete();
      displaySuccess(`Successfully removed the "${templateToRemove.attr('name')}" template`);

      // Refresh event
      await refreshTemplates();
      logResourceEventSuccess(eventName, templateToRemove);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  // Overwrite Message Template --------------------------------------------------------------------------------------------------------
  const overwriteTemplate = async (templateID, templateContent, templateSubject) => {
    const eventName = 'Clicked Overwrite Template';
    try {
      const templateToUpdate = templates.resources().list().find(t => t.id() === templateID);
      logResourceEvent(eventName, templateToUpdate);

      templateToUpdate.attr('content', templateContent);
      templateToUpdate.attr('subject', templateSubject);

      // Submit
      await templateToUpdate.submit();
      displaySuccess(`Successfully updated the "${templateToUpdate.attr('name')}" template`);

      // Refresh event
      await refreshTemplates();
      logResourceEventSuccess(eventName, templateToUpdate);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const togglePinTemplate = async (templateID, errorCallback) => {
    const eventName = 'Clicked Pin Template';
    try {
      // Get template to pin
      const templateToPin = templates.resources().list().find(t => t.id() === templateID);
      logResourceEvent(eventName, templateToPin);

      // Toggle Pin
      const isPinned = templateToPin.attr('isPinned') === 'true';
      templateToPin.attr('isPinned', isPinned ? 'false' : 'true')
        .attr('agentId', agentResource.id());

      // Submit
      await templateToPin.submit();
      displaySuccess(`Successfully ${isPinned ? 'unpinned' : 'pinned'} the "${templateToPin.attr('name')}" template`);

      // Refresh event
      await refreshTemplates();
      logResourceEventSuccess(eventName, templateToPin);
    } catch (err) {
      displayErrorMessage(err, err.errors);
      errorCallback();
    }
  };

  return {
    rootNodeProps,
    saveTemplate,
    removeTemplate,
    overwriteTemplate,
    togglePinTemplate,
    searchTemplates,
  };
};

export default useTemplateOptionsMenu;
